import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DashboardLayout } from "../common/page_component/index.js";
import ListPageHeader from "../common/ListPageHeader.js";
import Button from "../common/Button.js";
import Alert from "../common/Alert.js";
import Loader from "../common/Loader.js";
import { listPageWrapperClass } from "../common/uiClasses.js";
import { websiteService } from "../../services/websiteService.js";
import { CASE_CONFIG, formatWhen, statusClass } from "./WebsiteCaseList.js";

function Detail({ label, value }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</p>
      <p className="mt-1 text-sm sm:text-base font-semibold text-gray-900 break-words">{value || "—"}</p>
    </div>
  );
}

export default function WebsiteCaseView({ caseType }) {
  const config = CASE_CONFIG[caseType];
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [record, setRecord] = useState(null);
  const [status, setStatus] = useState("open");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await websiteService.getCase(caseType, caseId);
      const data = response.data.data;
      setRecord(data);
      setStatus(data.status);
      setNote(data.internal_note || "");
    } catch (err) {
      setError(err.response?.data?.error || "Record not found.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseType, caseId]);

  const save = async () => {
    setSaving(true);
    try {
      const response = await websiteService.updateCase(caseType, caseId, {
        status,
        internal_note: note,
      });
      setRecord(response.data.data);
      Alert.success("Update saved");
    } catch (err) {
      Alert.error(err.response?.data?.error || "Could not save the update");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div className={listPageWrapperClass}>
        <ListPageHeader
          title={record?.reference || config.title}
          icon={config.icon}
          subtitle={config.title}
          backTo={config.listPath}
          className="mb-2 sm:mb-3"
        >
          {record && (
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusClass(record.status)}`}>
              {record.status_label}
            </span>
          )}
        </ListPageHeader>

        {loading ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 flex items-center justify-center py-20">
            <Loader fullScreen={false} size="default" message={`Loading ${config.itemLabel}...`} />
          </div>
        ) : error ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 text-center py-16 text-red-600">{error}</div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 space-y-6">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <Detail label="Customer" value={record.customer_name} />
              <Detail label="Mobile" value={record.mobile} />
              <Detail label="Email" value={record.email} />
              <Detail label="Received" value={formatWhen(record.created_at)} />
              {caseType === "enquiry" && (
                <>
                  <Detail label="Pickup" value={record.pickup} />
                  <Detail label="Delivery" value={record.delivery} />
                  <Detail label="Packages" value={record.packages} />
                  <Detail label="Weight (kg)" value={record.weight} />
                  <Detail label="Preferred date" value={record.preferred_date} />
                  <Detail label="Company / GSTIN" value={record.company_name} />
                  <Detail label="Needs" value={(record.needs || []).join(", ")} />
                </>
              )}
              {caseType === "complaint" && (
                <>
                  <Detail label="LR / shipment number" value={record.lr_number} />
                  <Detail label="Booking date" value={record.booking_date} />
                  <Detail label="Problem" value={record.problem_type} />
                  <Detail label="Branch" value={record.branch_name} />
                </>
              )}
              {caseType === "message" && <Detail label="Topic" value={record.topic} />}
            </div>

            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                {caseType === "message" ? "Message" : caseType === "complaint" ? "What happened" : "Goods and notes"}
              </p>
              <p className="mt-2 whitespace-pre-wrap rounded-xl bg-gray-50 p-4 text-sm text-gray-800">
                {caseType === "message"
                  ? record.message
                  : caseType === "complaint"
                    ? record.issue
                    : [record.description, record.customer_notes].filter(Boolean).join("\n\n") || "—"}
              </p>
            </div>

            <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">
                  Status
                  <select
                    value={status}
                    onChange={(event) => setStatus(event.target.value)}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
                  >
                    <option value="open">Registered</option>
                    <option value="in_progress">Being looked into</option>
                    <option value="closed">Resolved</option>
                  </select>
                </label>
                <label className="block text-sm font-medium text-gray-700">
                  Internal note
                  <textarea
                    rows={5}
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                </label>
                <Button variant="primary" onClick={save} loading={saving} disabled={saving}>
                  Save update
                </Button>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">History</p>
                <ul className="mt-3 space-y-3">
                  {(record.events || []).map((event) => (
                    <li key={event.id} className="rounded-xl border border-gray-200 px-4 py-3">
                      <p className="text-sm font-medium text-gray-900">
                        {event.event_type === "status_changed" ? "Status changed" : event.event_type === "note" ? "Note" : "Received"}
                        {event.to_status ? ` · ${event.to_status.replace("_", " ")}` : ""}
                      </p>
                      {event.note && <p className="mt-1 text-sm text-gray-600">{event.note}</p>}
                      <p className="mt-1 text-xs text-gray-400">
                        {formatWhen(event.created_at)}
                        {(event.actor_name || event.actor_user_id) ? ` · ${event.actor_name || event.actor_user_id}` : ""}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        <div className="mt-4">
          <Button variant="secondary" onClick={() => navigate(config.listPath)}>
            Back to list
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
